import { useMemo } from 'react'
import {
  createPatientInputValidations,
  getAllValidationErrors,
  getValidationMessage,
  isFormValid,
  shouldShowValidationError,
  validateAttendants,
  validateAlternativeEmails,
  validateAlternativeMobiles,
} from '../utils/patientValidationUtils'

export const usePatientValidation = (
  patientDetails,
  makeAnonymousPatient,
  hideInputValidations = false,
  translateValidationMessage = (message) => message,
) => {
  const validations = useMemo(
    () => createPatientInputValidations(patientDetails, makeAnonymousPatient, hideInputValidations),
    [patientDetails, makeAnonymousPatient, hideInputValidations]
  )

  const validationErrors = useMemo(
    () => getAllValidationErrors(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient]
  )

  const attendantValidations = useMemo(
    () => validateAttendants(patientDetails?.attendants),
    [patientDetails?.attendants]
  )

  const alternativeMobileValidations = useMemo(
    () => validateAlternativeMobiles(patientDetails?.altMobiles),
    [patientDetails?.altMobiles]
  )

  const alternativeEmailValidations = useMemo(
    () => validateAlternativeEmails(patientDetails?.altEmails),
    [patientDetails?.altEmails]
  )

  const isValid = useMemo(
    () => isFormValid(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient]
  )

  const hasErrors = useMemo(
    () =>
      validationErrors.length > 0 ||
      attendantValidations.length > 0 ||
      alternativeMobileValidations.length > 0 ||
      alternativeEmailValidations.length > 0,
    [validationErrors, attendantValidations, alternativeMobileValidations, alternativeEmailValidations]
  )

  const getFieldValidation = (fieldName) => {
    return {
      isValid: validations[fieldName] !== false,
      showError: shouldShowValidationError(validations[fieldName], hideInputValidations),
      errorMessage:
        validations[fieldName] === false
          ? translateValidationMessage(getValidationMessage(fieldName, patientDetails))
          : null,
    }
  }

  const getAttendantValidation = (index) => {
    const validation = attendantValidations.find(v => v.index === index)
    return {
      isValid: !validation,
      showError: !!validation,
      errorMessage: validation?.message ? translateValidationMessage(validation.message) : null,
    }
  }

  const getAlternativeMobileValidation = (index) => {
    const validation = alternativeMobileValidations.find(v => v.index === index)
    return {
      isValid: !validation,
      showError: !!validation,
      errorMessage: validation?.message ? translateValidationMessage(validation.message) : null,
    }
  }

  const getAlternativeEmailValidation = (index) => {
    const validation = alternativeEmailValidations.find(v => v.index === index)
    return {
      isValid: !validation,
      showError: !!validation,
      errorMessage: validation?.message ? translateValidationMessage(validation.message) : null,
    }
  }

  const getAllErrors = () => {
    return {
      fieldErrors: validationErrors,
      attendantErrors: attendantValidations,
      alternativeMobileErrors: alternativeMobileValidations,
      alternativeEmailErrors: alternativeEmailValidations,
    }
  }

  const getErrorSummary = () => {
    const allErrors = getAllErrors()
    const totalErrors =
      allErrors.fieldErrors.length +
      allErrors.attendantErrors.length +
      allErrors.alternativeMobileErrors.length +
      allErrors.alternativeEmailErrors.length

    return {
      totalErrors,
      hasFieldErrors: allErrors.fieldErrors.length > 0,
      hasAttendantErrors: allErrors.attendantErrors.length > 0,
      hasAlternativeMobileErrors: allErrors.alternativeMobileErrors.length > 0,
      hasAlternativeEmailErrors: allErrors.alternativeEmailErrors.length > 0,
      summary: `${totalErrors} validation error${totalErrors !== 1 ? 's' : ''} found`,
    }
  }

  return {
    validations,
    validationErrors,
    attendantValidations,
    alternativeMobileValidations,
    alternativeEmailValidations,
    isValid,
    hasErrors,
    getFieldValidation,
    getAttendantValidation,
    getAlternativeMobileValidation,
    getAlternativeEmailValidation,
    getAllErrors,
    getErrorSummary,
  }
}
